import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember,
  GetGuestsByEventIdAndMobile, AddGuestToFunctions, AddOrMoveFamilyMember, UpdateGuest,
  SetGuestFunctions, GetTeamByEventId,
} from '../../api/api';
import {
  isMarriageEventType, normaliseText, isPastFunction, tenDigits, nameInput, isWeddingFunction, toggleWithWedding,
  newGuestFunctionIds,
} from '../../utils/helpers';
import { contactsSupported, pickContactFromDevice, bulkContactsSupported, getDeviceContacts } from '../../utils/contacts';
import { shareInvite } from '../../utils/whatsappInvite';
import { useQuickAdd } from './useQuickAdd';
import { usePeopleSearch } from './usePeopleSearch';
import { useContactSuggest } from './useContactSuggest';

export const MAX_FAMILY_SIZE = 9; // 1 family head + up to 8 other members
// A new family starts with this many blank member lines to type into (blank
// lines are ignored on save), so a household is entered without extra clicks.
const FAMILY_START_ROWS = 6;   // the grid is short now - show six waiting lines, blank ones are skipped
const blankMember = () => ({ Surname: '', GuestName: '', MobileNumber: '', FunctionIds: [] });
const blankMembers = () => Array.from({ length: FAMILY_START_ROWS }, blankMember);

// Last-used functions / side / visibility, remembered per event so a member
// adding 50 guests for the same functions ticks them once (localStorage).
const defaultsKey = (eventId) => `se_guest_defaults:${eventId}`;
const readDefaults = (eventId) => { try { return JSON.parse(localStorage.getItem(defaultsKey(eventId))) || {}; } catch { return {}; } };
const writeDefaults = (eventId, d) => { try { localStorage.setItem(defaultsKey(eventId), JSON.stringify(d)); } catch { /* ignore */ } };
export const INVITE_COOLDOWN_SECONDS = 10;
const ten = tenDigits;
const emptyToNull = (obj) => {
  const out = {};
  for (const k of Object.keys(obj)) out[k] = obj[k] === '' ? null : obj[k];
  return out;
};
const blankForm = () => ({
  FunctionId: '', FunctionIds: [], Surname: '', GuestName: '', FatherHusband: '', MobileNumber: '',
  // A business associate is invited as a firm: the card names the owner with
  // the company under it (GuestType is 'Family' for everyone else).
  GuestType: 'Family', CompanyName: '', City: '',
  Side: 'Common', Relation: '', GroupName: '', EmailId: '', ExpectedCount: 1,
  Visibility: 'Private', InviteStatus: 'Pending', InvitedVia: '', Remarks: '',
  ExtraCards: '', // extra invitation cards the head asked for (names unknown, Reception only)
  // Invitation card (Cards page fields): own card, or on another family head's card.
  CardMode: 'own', CardFamilyId: '', CardHeadText: '', CardName: '', CardDeliveredByName: '',
  // Name on the card: follows the guest's own name until typed over or cleared (×).
  CardNameAuto: true,
});

// Rows of ONE person across several functions collapse into one card. A
// mobile number identifies a person; without one two guests of the same name
// cannot be told apart, so each row stays its own card rather than merging two
// different Rameshes (numbers are optional now).
const guestGroupKey = (row) => {
  const owner = row.CreatedBy || 'admin';
  const mobile = String(row.MobileNumber || '').trim();
  if (mobile) return `${owner}:mobile:${mobile}`;
  return `guest:${row.GuestId}`;
};

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

const combineGuestRows = (list) => {
  const groups = new Map();
  list.forEach(row => {
    const key = guestGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        ...row,
        _rows: [],
        _guestIds: [],
        _functionIds: [],
        FunctionNames: '',
      });
    }
    const group = groups.get(key);
    group._rows.push(row);
    group._guestIds.push(row.GuestId);
    rowFunctionIds(row).forEach(id => {
      if (!group._functionIds.some(existing => String(existing) === String(id))) group._functionIds.push(id);
    });
    const names = String(row.FunctionNames || row.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean);
    names.forEach(name => {
      if (!group.FunctionNames.split(', ').includes(name)) {
        group.FunctionNames = group.FunctionNames ? `${group.FunctionNames}, ${name}` : name;
      }
    });
    group.ExpectedCount = Math.max(Number(group.ExpectedCount || 1), Number(row.ExpectedCount || 1));
  });
  return Array.from(groups.values());
};

// Sortable columns of the guest list: key → value extractor. Order of
// SORT_OPTIONS is what the "Sort" dropdown shows.
const lc = (v) => (v || '').toString().trim().toLowerCase();
// own = in no family · head = heads a family · member = on someone else's card
const cardRole = (r) => { const f = Number(r.FamilyId || 0); return !f ? 'own' : (f === Number(r.GuestId) ? 'head' : 'member'); };
const sideKey = (s) => (s === 'Male' ? 'Groom' : s === 'Female' ? 'Bride' : (s || 'Common'));
export const SORT_KEYS = {
  name: (r) => lc([r.Surname, r.GuestName, r.FatherHusband].filter(Boolean).join(' ')),
  firstName: (r) => lc(r.GuestName),
  mobile: (r) => lc(r.MobileNumber),
  function: (r) => lc(r.FunctionNames || r.FunctionName),
  side: (r) => lc(r.Side),
  pax: (r) => Number(r.ExpectedCount || 1),
  visibility: (r) => lc(r.Visibility || 'Private'),
  addedBy: (r) => lc(r.AddedByName || r.InvitedByName),
  status: (r) => lc(r.InviteStatus),
  rsvp: (r) => lc(r.RSVPStatus),
  city: (r) => lc(r.City),
  guestType: (r) => lc(r.GuestType || 'Family'),
  added: (r) => lc(r.CreatedDate),
};
export const SORT_OPTIONS = [
  ['name', 'Surname + Name'], ['firstName', 'First name'], ['mobile', 'Mobile'], ['function', 'Function'],
  ['side', 'Side'], ['pax', 'Persons'], ['visibility', 'Visibility'], ['addedBy', 'Added by'],
  ['status', 'Status'], ['rsvp', 'RSVP'], ['city', 'City'], ['added', 'Date added'],
];

// All Members logic — shared by the desktop and mobile UIs.
export function useMembers(event) {
  const { user, organiser, isAdmin, can, mySide, sideLocked } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = can('CanMembers');
  const canAdd = can('CanMembersAdd') || can('CanMembers');
  const canEdit = can('CanMembersEdit') || can('CanMembers');
  const canDelete = can('CanMembersDelete') || can('CanMembers');
  const mine = (row) => isAdmin || String(row.CreatedBy) === String(me);

  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // `?fn=<FunctionId>` (from the Functions / Headcount drill-in) preselects the filter.
  const [searchParams] = useSearchParams();
  // fnFilter: '' all · a FunctionId · '__all' invited to every open function ·
  // '__some' missing at least one open function. fnMode (with a FunctionId):
  // 'in' invited to it · 'out' NOT invited to it.
  const [fnFilter, setFnFilter] = useState(() => searchParams.get('fn') || '');
  const [fnMode, setFnMode] = useState('in');
  const [sideFilter, setSideFilter] = useState('');
  // Card filter: '' all guests · 'cards' own card + family heads (one row per
  // card) · 'own' only guests on their own card (in no family).
  const [cardFilter, setCardFilter] = useState('');
  // Whatever side this user belongs to (My Profile) is the side of the guests
  // they add — the guest screens never ask.
  const guestSide = mySide || 'Common';
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);
  // Two-sided event: an ordinary member is pinned to one side (their
  // Evt_UserEventsMapping.Side) and every guest they add gets it — no picker, no
  // dashboard toggle. Admins keep both sides. A member whose side an admin has
  // not assigned yet cannot add guests at all (sideMissing).
  // The side comes from the user (My Profile) for every role: the guest form
  // shows it, never asks. An admin set to 'Both/Common' adds Common guests.
  const lockedSide = isMarriage ? mySide : null;
  const sideMissing = isMarriage && sideLocked && !mySide;
  const sideOptions = isMarriage ? (lockedSide ? [lockedSide] : ['Common', 'Groom', 'Bride']) : ['Common'];
  const defaultSide = isMarriage ? (lockedSide || guestSide) : 'Common';

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => ({ ...blankForm(), Side: defaultSide }));
  const [party, setParty] = useState([]);
  const [familyMode, setFamilyMode] = useState(false);
  const [addMode, setAddMode] = useState('new');
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [checkedMobile, setCheckedMobile] = useState('');
  const [inviteRow, setInviteRow] = useState(null);
  const [inviteSharing, setInviteSharing] = useState('');
  const [inviteCooldown, setInviteCooldown] = useState(0);
  // How many guests were added since the form was opened ("Save & add next").
  const [addedThisSession, setAddedThisSession] = useState(0);
  // The Wedding is ticked with any other function unless it was unticked by
  // hand for this guest (see toggleWithWedding in helpers).
  const [weddingOff, setWeddingOff] = useState(false);
  // Set when Add Guest found the person already in this event and switched to
  // editing them (their name) - the form says so instead of blocking.
  const [existingNotice, setExistingNotice] = useState('');
  // Team members, for "card delivered by" suggestions.
  const [team, setTeam] = useState([]);

  // ── Bulk "import from contacts" state ──────────────────────────────────────
  const canBulkImport = bulkContactsSupported();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [deviceContacts, setDeviceContacts] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkFunctionIds, setBulkFunctionIds] = useState([]);
  const [bulkSide, setBulkSide] = useState(() => defaultSide);
  const [bulkVisibility, setBulkVisibility] = useState('Private');
  const [bulkWeddingOff, setBulkWeddingOff] = useState(false);

  useEffect(() => {
    if (inviteCooldown <= 0) return undefined;
    const timer = setTimeout(() => setInviteCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [inviteCooldown]);

  const fnName = useCallback(
    (id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '—',
    [functions]
  );

  // Every family of this event, for "put this guest into a family": the head,
  // how many people are in it, and a label to show in the picker.
  const familyOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach(r => {
      const famId = Number(r.FamilyId || 0);
      if (!famId) return;
      if (!byId.has(famId)) byId.set(famId, { familyId: famId, head: null, size: 0, seen: new Set() });
      const fam = byId.get(famId);
      if (fam.seen.has(String(r.GuestId))) return;
      fam.seen.add(String(r.GuestId));
      fam.size += 1;
      if (String(r.GuestId) === String(famId)) fam.head = r;
    });
    return [...byId.values()]
      .filter(f => f.head)
      .map(f => ({
        familyId: f.familyId,
        head: f.head,
        size: f.size,
        label: [f.head.Surname, f.head.GuestName].filter(Boolean).join(' ') || 'Family',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // "On the card of …": any existing family head, or any individual guest -
  // picking an individual makes them the head of a new card (value 'g:<GuestId>').
  const cardHeadOptions = useMemo(() => {
    const fams = familyOptions.map(f => ({ value: String(f.familyId), size: f.size, head: f.head, label: `${f.label} · family of ${f.size}` }));
    const singles = combineGuestRows(rows.filter(r => !Number(r.FamilyId || 0))).map(g => ({
      value: `g:${g.GuestId}`, size: 1, head: g, guestIds: g._guestIds || [g.GuestId],
      label: `${[g.Surname, g.GuestName, g.FatherHusband].filter(Boolean).join(' ') || 'Guest'} · own card${g.MobileNumber ? ` · ${g.MobileNumber}` : ''}`,
    }));
    return [...fams, ...singles].sort((a, b) => a.label.localeCompare(b.label));
  }, [familyOptions, rows]);

  const loadFunctions = useCallback(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => {});
  }, [event]);

  // Functions that can still be invited to (date today or later / no date).
  const invitableFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);

  const refresh = useCallback((silent = false) => {
    if (silent !== true) setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load members'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);
  useEffect(() => {
    GetTeamByEventId({ EventId: event.EventId }).then(d => setTeam(Array.isArray(d) ? d : [])).catch(() => setTeam([]));
  }, [event]);
  useEffect(() => { refresh(); }, [refresh]);

  const distinctPeople = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.MobileNumber || `name:${r.GuestName}`;
      if (!map.has(key)) map.set(key, r);
    });
    return Array.from(map.values());
  }, [rows]);

  const addersByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (!r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      m.get(r.MobileNumber).add(String(r.CreatedBy));
    });
    return m;
  }, [rows]);
  const adderCount = (row) => (row.MobileNumber ? (addersByMobile.get(row.MobileNumber)?.size || 1) : 1);

  // Sort: { key, dir }. Keys map to a comparable value per row (see SORT_KEYS).
  // Clicking the same column header again flips the direction.
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // Who added the guests: one entry per person who has guests in this event,
  // with the number so two Sanjays can be told apart. Picking one turns the
  // list into THAT person's list - the cleanest way to work through a share of
  // the invitations - and the printed report then names them in its header
  // instead of repeating the name on every line.
  const adderOptions = useMemo(() => {
    const byUser = new Map();
    rows.forEach(r => {
      const id = String(r.CreatedBy || '');
      if (!id) return;
      if (!byUser.has(id)) {
        byUser.set(id, {
          value: id,
          name: (r.AddedByName || r.InvitedByName || '').trim() || `User ${id}`,
          mobile: String(r.AddedByMobile || '').trim(),
          count: 0,
        });
      }
      byUser.get(id).count += 1;
    });
    return [...byUser.values()]
      .map(u => ({ ...u, label: `${u.name}${u.mobile ? ' - ' + u.mobile : ''}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const [adderFilter, setAdderFilter] = useState('');
  const adder = adderOptions.find(u => u.value === String(adderFilter)) || null;

  // all · family (in a household) · individual (on their own) · business (a firm)
  const [guestTypeFilter, setGuestTypeFilter] = useState('all');
  const [visFilter, setVisFilter] = useState('');   // '' all · 'public' · 'private'
  const isBusiness = (r) => String(r?.GuestType || 'Family') === 'Business';

  const filtered = useMemo(() => {
    let list = rows;
    if (fnFilter === '__all' || fnFilter === '__some') {
      const open = functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId));
      const hasAll = (r) => { const ids = rowFunctionIds(r); return open.every(id => ids.includes(id)); };
      list = list.filter(r => (fnFilter === '__all' ? hasAll(r) : !hasAll(r)));
    } else if (fnFilter) {
      const want = fnMode !== 'out';
      list = list.filter(r => rowFunctionIds(r).includes(String(fnFilter)) === want);
    }
    if (adderFilter) list = list.filter(r => String(r.CreatedBy || '') === String(adderFilter));
    // Groom / Bride buttons; the old Male / Female values count as Groom / Bride.
    if (sideFilter) list = list.filter(r => sideKey(r.Side) === sideFilter);
    if (cardFilter) list = list.filter(r => cardRole(r) === 'own' || (cardFilter === 'cards' && cardRole(r) === 'head'));
    // Guest type: 'all' · 'relative' (stored 'Family') · 'friend' · 'business'.
    if (guestTypeFilter && guestTypeFilter !== 'all') {
      const want = guestTypeFilter === 'relative' ? 'family' : guestTypeFilter;
      list = list.filter(r => String(r.GuestType || 'Family').toLowerCase() === want);
    }
    // Public / Private dropdown ('' = both). Public = Visibility 'Common'.
    if (visFilter) list = list.filter(r => ((r.Visibility || 'Private') === 'Common') === (visFilter === 'public'));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(r => JSON.stringify(r).toLowerCase().includes(s));
    }
    const get = SORT_KEYS[sort.key] || SORT_KEYS.name;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return combineGuestRows(list).sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }, [rows, functions, fnFilter, fnMode, adderFilter, sideFilter, cardFilter, guestTypeFilter, visFilter, q, sort]);
  const visCounts = useMemo(() => {
    const c = { all: 0, public: 0, private: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[(g.Visibility || 'Private') === 'Common' ? 'public' : 'private'] += 1; });
    return c;
  }, [rows]);
  const typeCounts = useMemo(() => {
    const c = { all: 0, relative: 0, friend: 0, business: 0 };
    combineGuestRows(rows).forEach(g => {
      const t = String(g.GuestType || 'Family').toLowerCase();
      c.all += 1;
      const k = t === 'family' ? 'relative' : t;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [rows]);
  // Guests invited to each function (all guests this user sees, before any
  // filter) - shown next to every function name: "Wedding (123)".
  const fnCounts = useMemo(() => {
    const m = new Map();
    rows.forEach(r => rowFunctionIds(r).forEach(id => m.set(id, (m.get(id) || 0) + 1)));
    return m;
  }, [rows]);
  const fnCount = useCallback((id) => fnCounts.get(String(id)) || 0, [fnCounts]);
  // Words for the function filter (toolbar tooltip, export header).
  const fnFilterLabel = fnFilter === '__all' ? 'Invited to all functions'
    : fnFilter === '__some' ? 'Missing some functions'
      : fnFilter ? `${fnMode === 'out' ? 'Not invited to' : 'Invited to'} ${fnName(fnFilter)}` : 'All functions';

  // ── Desktop list view: 'people' (all guests, a row each), 'families' (only
  // families: the head, its members folded underneath) or 'individuals' (only
  // guests in no family). Remembered per browser.
  const [listView, setListViewRaw] = useState(() => {
    try { const v = localStorage.getItem('se_guest_view'); return ['families', 'individuals', 'business'].includes(v) ? v : 'people'; } catch { return 'people'; }
  });
  const setListView = (v) => { setListViewRaw(v); try { localStorage.setItem('se_guest_view', v); } catch { /* ignore */ } };
  // The filtered + sorted list grouped by family, in list order. A family whose
  // head is filtered out is led by its first listed member (headMissing).
  const familyGroups = useMemo(() => {
    const groups = [];
    const byFam = new Map();
    filtered.forEach(r => {
      const f = Number(r.FamilyId || 0);
      if (!f) { groups.push({ key: `g${r.GuestId}`, head: r, members: [] }); return; }
      if (!byFam.has(f)) { const g = { key: `f${f}`, familyId: f, head: null, members: [] }; byFam.set(f, g); groups.push(g); }
      const g = byFam.get(f);
      if (Number(r.GuestId) === f) g.head = r; else g.members.push(r);
    });
    groups.forEach(g => { if (!g.head) { g.head = g.members.shift(); g.headMissing = true; } });
    return groups.filter(g => g.familyId);
  }, [filtered]);
  const individualRows = useMemo(() => filtered.filter(r => !Number(r.FamilyId || 0)), [filtered]);
  // The firms: a business associate is one row (the owner, with his company).
  const businessRows = useMemo(() => filtered.filter(isBusiness), [filtered]);   // eslint-disable-line react-hooks/exhaustive-deps
  const viewCounts = {
    people: filtered.length, families: familyGroups.length,
    individuals: individualRows.length, business: businessRows.length,
  };

  // ── Family cards (mobile list) ────────────────────────────────────────────
  // Group the (already filtered + sorted) rows into cards: individuals as-is,
  // family members collapsed under their head. Order follows `filtered`.
  const familyCards = useMemo(() => {
    const cards = [];
    const families = new Map();
    const allFamilyMembers = new Map();
    rows.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) return;
      if (!allFamilyMembers.has(familyId)) allFamilyMembers.set(familyId, []);
      if (!allFamilyMembers.get(familyId).some(m => String(m.GuestId) === String(row.GuestId))) allFamilyMembers.get(familyId).push(row);
    });
    filtered.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) { cards.push({ type: 'guest', key: `guest-${row.GuestId}`, row }); return; }
      if (!families.has(familyId)) {
        const family = { type: 'family', key: `family-${familyId}`, familyId, members: [] };
        families.set(familyId, family);
        cards.push(family);
      }
      families.get(familyId).members = allFamilyMembers.get(familyId) || [row];
    });
    families.forEach(family => {
      family.members.sort((a, b) => {
        if (String(a.GuestId) === String(family.familyId)) return -1;
        if (String(b.GuestId) === String(family.familyId)) return 1;
        return Number(a.GuestId) - Number(b.GuestId);
      });
      family.head = family.members.find(r => String(r.GuestId) === String(family.familyId)) || family.members[0];
    });
    return cards;
  }, [filtered, rows]);
  const visibleCards = useMemo(() => familyCards.filter(card =>
    guestTypeFilter === 'all'
      || (guestTypeFilter === 'family' && card.type === 'family')
      || (guestTypeFilter === 'individual' && card.type === 'guest')
      || (guestTypeFilter === 'business' && isBusiness(card.type === 'family' ? card.head : card.row))
  ), [familyCards, guestTypeFilter]);

  // Family-member actions are rate-limited to one per 5 s (accidental double taps).
  const [actionCooldown, setActionCooldown] = useState(0);
  useEffect(() => {
    if (actionCooldown <= 0) return undefined;
    const timer = setTimeout(() => setActionCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [actionCooldown]);
  const beginFamilyAction = (action) => {
    if (actionCooldown > 0) return;
    setActionCooldown(5);
    action();
  };
  // Open the phone dialer (APK bridge when present, tel: otherwise).
  const callGuest = (row) => {
    const mobile = String(row.MobileNumber || '').replace(/\D/g, '');
    if (!mobile) return;
    if (window.AndroidDialer?.openDialer) window.AndroidDialer.openDialer(mobile);
    else window.location.href = `tel:${mobile}`;
  };

  // Guests per card role (whole list) for the All / Own + Head / Only own buttons.
  const cardCounts = useMemo(() => {
    const c = { all: 0, own: 0, head: 0, member: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[cardRole(g)] += 1; });
    return { all: c.all, cards: c.own + c.head, own: c.own };
  }, [rows]);

  // Guests per side (whole list, one per person) for the Groom / Bride buttons.
  const sideCounts = useMemo(() => {
    const c = { all: 0, Groom: 0, Bride: 0, Common: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[sideKey(g.Side)] = (c[sideKey(g.Side)] || 0) + 1; });
    return c;
  }, [rows]);

  const stats = useMemo(() => {
    const persons = filtered.reduce((sum, r) => sum + Number(r.ExpectedCount || 1), 0);
    const entries = filtered.reduce((sum, r) => sum + (r._rows?.length || 1), 0);
    const bride = filtered.filter(r => r.Side === 'Bride' || r.Side === 'Female').length;
    const groom = filtered.filter(r => r.Side === 'Groom' || r.Side === 'Male').length;
    return { entries, parties: filtered.length, persons, bride, groom };
  }, [filtered]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedFunctionIds = useMemo(() => (Array.isArray(form.FunctionIds) ? form.FunctionIds.map(String) : []), [form.FunctionIds]);
  const toggleFunction = (functionId) => {
    const id = String(functionId);
    const fn = functions.find(f => String(f.FunctionId) === id);
    if (fn && isPastFunction(fn)) return; // over — cannot be (un)ticked
    const res = toggleWithWedding(selectedFunctionIds, id, functions, weddingOff);
    setWeddingOff(res.weddingOff);
    setForm(f => ({ ...f, FunctionIds: res.ids, FunctionId: res.ids[0] || '' }));
  };
  // What the guest form offers: every open function, plus (when editing) the
  // past functions this guest is already in — shown disabled, so history is
  // visible but nothing new can be added to a function that is over.
  const pickerFunctions = useMemo(() => functions
    .filter(f => !isPastFunction(f) || (editing && selectedFunctionIds.includes(String(f.FunctionId))))
    .map(f => ({ ...f, past: isPastFunction(f) })),
    [functions, editing, selectedFunctionIds]);

  // Tick / untick every open function in one tap (mobile chips).
  const allOpenIds = useMemo(() => functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId)), [functions]);
  const allFunctionsSelected = allOpenIds.length > 0 && allOpenIds.every(id => selectedFunctionIds.includes(id));
  const toggleAllFunctions = () => setForm(f => {
    const next = allFunctionsSelected ? [] : allOpenIds;
    return { ...f, FunctionIds: next, FunctionId: next[0] || '' };
  });

  // A family member may be invited to FEWER functions than the head (children
  // to the Haldi only, a colleague to the reception). An empty FunctionIds
  // means "same as the head", which is the common case.
  const addParty = () => setParty(p => (p.length >= MAX_FAMILY_SIZE - 1 ? p : [...p, blankMember()]));
  const togglePartyFunction = (i, functionId) => setParty(p => p.map((m, idx) => {
    if (idx !== i) return m;
    const own = Array.isArray(m.FunctionIds) && m.FunctionIds.length ? m.FunctionIds.map(String) : selectedFunctionIds;
    // Wedding rule, limited to the head's functions (a member never gets more).
    const headPool = functions.filter(f => selectedFunctionIds.includes(String(f.FunctionId)));
    const res = toggleWithWedding(own, functionId, headPool, !!m.weddingOff);
    const next = res.ids;
    // Back to the head's full set → store nothing, so it keeps following it.
    const same = next.length === selectedFunctionIds.length && selectedFunctionIds.every(x => next.includes(x));
    return { ...m, FunctionIds: same ? [] : next, weddingOff: res.weddingOff };
  }));
  // What this member is actually invited to, head's list included.
  const partyFunctionIds = (m) => (Array.isArray(m.FunctionIds) && m.FunctionIds.length
    ? m.FunctionIds.map(String).filter(id => selectedFunctionIds.includes(id))
    : selectedFunctionIds);
  const setPartyField = (i, k, v) => setParty(p => p.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const removeParty = (i) => setParty(p => p.filter((_, idx) => idx !== i));

  const openAdd = (mode = 'individual') => {
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const remembered = readDefaults(event.EventId);
    // Preselect: the filtered function, else the functions used last time (still open).
    const openIds = new Set(functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId)));
    const rememberedIds = (remembered.FunctionIds || []).map(String).filter(id => openIds.has(id));
    const initialFunctions = newGuestFunctionIds(preset && !isPastFunction(preset) ? [String(fnFilter)] : rememberedIds, functions);
    setAddedThisSession(0);
    setWeddingOff(false);
    setForm({
      ...blankForm(),
      Side: lockedSide || (isMarriage && remembered.Side) || defaultSide,
      Visibility: remembered.Visibility || 'Private',
      FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions,
    });
    setFamilyMode(mode === 'family');
    setParty(mode === 'family' ? blankMembers() : []);
    setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(null); setOpen(true);
    setExistingNotice('');
    setFamilyHeadNote('');
    people.clear();
  };
  // Right-click → "Add family members (make head)": Add Family with this guest
  // already filled in as the head (name, number, their functions). Saving
  // merges as usual (saveFamilyMerged): the guest becomes the head, existing
  // members of their family stay. A guest on SOMEONE ELSE's card is warned
  // first - making them a head takes them out of that family.
  const [familyHeadNote, setFamilyHeadNote] = useState('');
  const openFamilyFor = (row) => {
    if (!(mine(row) && canAdd)) { toast('Only the member who added this guest (or an admin) can do this'); return; }
    const who = [row.Surname, row.GuestName].filter(Boolean).join(' ') || 'This guest';
    const famId = Number(row.FamilyId || 0);
    const isHead = famId && famId === Number(row.GuestId);
    let note = '';
    if (famId && !isHead) {
      const fam = familyOptions.find(f => Number(f.familyId) === famId);
      const headName = fam ? [fam.head.Surname, fam.head.GuestName].filter(Boolean).join(' ') : 'another guest';
      if (!window.confirm(`${who} is already on ${headName}'s card (${headName} is the head of that family).\n\nMake ${who} the head of a family of their own instead? They will leave ${headName}'s family.`)) return;
      note = `${who} leaves ${headName}'s family and becomes the head of a new one.`;
    } else if (isHead) {
      const size = rows.filter(x => Number(x.FamilyId || 0) === famId).length;
      note = `${who} is already the head of a family of ${size} - the members you add here join it; existing members stay.`;
    }
    openAdd('family');
    const ids = [...new Set([...rowFunctionIds(row), ...(row._functionIds || [])].map(String))];
    setForm(f => ({
      ...f,
      Surname: row.Surname || '', GuestName: row.GuestName || '', FatherHusband: row.FatherHusband || '',
      MobileNumber: row.MobileNumber || '',
      FunctionIds: ids.length ? ids : f.FunctionIds, FunctionId: (ids[0] || f.FunctionId),
      Visibility: row.Visibility || f.Visibility,
    }));
    setFamilyHeadNote(note);
  };

  // Back-key support: reset the form to blank but keep it open (clear-then-close).
  const clearForm = () => {
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const initialFunctions = newGuestFunctionIds(preset && !isPastFunction(preset) ? [String(fnFilter)] : [], functions);
    setWeddingOff(false);
    setForm({ ...blankForm(), Side: lockedSide || defaultSide, FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions });
    setParty(familyMode ? blankMembers() : []);
    setAddMode('new'); setDupes([]); setCheckedMobile('');
  };
  // Dirty = the user typed anything meaningful (lead fields or any party rows).
  const isDirty = () => {
    const b = blankForm();
    const changed = ['GuestName', 'Surname', 'FatherHusband', 'MobileNumber', 'Relation', 'GroupName', 'Remarks']
      .some(k => String(form[k] ?? '') !== String(b[k] ?? ''));
    return changed || party.some(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
  };
  const openEdit = (row, { existing = false } = {}) => {
    const functionIds = rowFunctionIds(row);
    const famId = Number(row.FamilyId || 0);
    const onOtherCard = !!famId && famId !== Number(row.GuestId);
    setExistingNotice(existing ? ([row.Surname, row.GuestName, row.FatherHusband].filter(Boolean).join(' ') || 'This person') : '');
    // A guest saved WITHOUT the Wedding had it removed on purpose - keep it off.
    setWeddingOff(functionIds.length > 0 && !functionIds.some(id => isWeddingFunction(functions.find(f => String(f.FunctionId) === String(id)))));
    setForm({
      FunctionId: functionIds[0] || '', FunctionIds: functionIds,
      Surname: row.Surname ?? '', GuestName: row.GuestName ?? '',
      FatherHusband: row.FatherHusband ?? '', MobileNumber: row.MobileNumber ?? '',
      Side: row.Side ?? 'Common', Relation: row.Relation ?? '', GroupName: row.GroupName ?? '',
      EmailId: row.EmailId ?? '', ExpectedCount: 1,
      GuestType: row.GuestType || 'Family', CompanyName: row.CompanyName ?? '', City: row.City ?? '',
      Visibility: row.Visibility ?? 'Private',
      InviteStatus: row.InviteStatus ?? 'Pending', InvitedVia: row.InvitedVia ?? '', Remarks: row.Remarks ?? '',
      ExtraCards: Number(row.ExtraCards || 0) ? String(row.ExtraCards) : '',
      CardMode: onOtherCard ? 'family' : 'own', CardFamilyId: onOtherCard ? String(famId) : '',
      CardName: row.CardName ?? '', CardDeliveredByName: row.CardDeliveredByName ?? '',
      CardNameAuto: !String(row.CardName || '').trim(),
    });
    setFamilyMode(false);
    setParty([]); setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(row); setOpen(true);
  };

  // Type-ahead help: the surnames and first names already in this event, so a
  // family is spelt the same way every time instead of Punjabi / punjabee.
  const knownSurnames = useMemo(() => [...new Set(rows.map(r => String(r.Surname || '').trim()).filter(Boolean))].sort(), [rows]);
  const knownNames = useMemo(() => [...new Set(rows.map(r => String(r.GuestName || '').trim()).filter(Boolean))].sort(), [rows]);

  // Same person twice? A guest is "already saved" when the SAME surname+name,
  // or the SAME number, is already invited to one of the functions being
  // ticked - whoever added it. Re-inviting the same person to a DIFFERENT
  // function is normal and stays allowed.
  const personKey = (surname, name) => `${lc(surname)}|${lc(name)}`;
  const nameMatches = (surname, name, exceptGuestId) => {
    const key = personKey(surname, name);
    if (key === '|') return [];
    return rows.filter(r => personKey(r.Surname, r.GuestName) === key
      && String(r.GuestId) !== String(exceptGuestId || ''));
  };
  const clashingFunctions = (list) => list.filter(r => rowFunctionIds(r).some(id => selectedFunctionIds.includes(String(id))));
  const whoAdded = (r) => (String(r.CreatedBy) === String(me) ? 'you' : ((r.AddedByName || r.InvitedByName || '').trim() || 'another member'));

  const canImport = contactsSupported();
  // "Card delivered by" suggestions: the team + names already used.
  const deliveryNames = useMemo(() => [...new Set([
    ...team.map(t => [t.Name, t.Surname].filter(Boolean).join(' ').trim()),
    ...rows.map(r => String(r.CardDeliveredByName || '').trim()),
  ].filter(Boolean))].sort(), [team, rows]);

  // One contact can hold a dozen numbers (both SIMs, the office, the sons).
  // When it does we ask which one to invite instead of guessing the first;
  // `numberChoice` drives that little chooser - { contact, target } where
  // target is 'head' or the family-member index.
  const [numberChoice, setNumberChoice] = useState(null);

  const applyContact = async (c, mobile, target) => {
    if (target === 'head') {
      setForm(f => ({
        ...f,
        Surname: normaliseText(c.surname) || f.Surname,
        GuestName: normaliseText(c.name) || f.GuestName,
        FatherHusband: normaliseText(c.fatherHusband) || f.FatherHusband,
        MobileNumber: mobile || f.MobileNumber,
      }));
      if (mobile) await checkMobile(mobile);
      return;
    }
    setParty(p => p.map((m, idx) => (idx === target ? {
      ...m,
      Surname: normaliseText(c.surname) || m.Surname,
      GuestName: normaliseText(c.name) || m.GuestName,
      MobileNumber: mobile || m.MobileNumber,
    } : m)));
  };

  const importInto = async (target) => {
    try {
      const c = await pickContactFromDevice();
      if (!c) return;
      const numbers = c.numbers || (c.mobile ? [{ number: c.mobile, label: '', mobile: true }] : []);
      if (numbers.length > 1) { setNumberChoice({ contact: c, numbers, target }); return; }
      await applyContact(c, numbers[0]?.number || c.mobile, target);
    } catch (e) { toast(e.message || 'Could not read contacts'); }
  };

  const importContact = () => importInto('head');
  // The chooser calls this with the number the user tapped.
  const chooseContactNumber = async (mobile) => {
    const pending = numberChoice;
    setNumberChoice(null);
    if (pending) await applyContact(pending.contact, mobile, pending.target);
  };

  // Same phone-book pick, but into one family-member row: a family is usually
  // several entries of the same phone book, so typing them out is wasted work.
  const importPartyContact = (i) => importInto(i);

  const pickExisting = (guestId) => {
    const r = rows.find(x => String(x.GuestId) === String(guestId));
    if (!r) return;
    setForm(f => ({
      ...f,
      Surname: r.Surname ?? '', GuestName: r.GuestName ?? '', FatherHusband: r.FatherHusband ?? '',
      MobileNumber: r.MobileNumber ?? '', Side: r.Side ?? 'Common',
      Relation: r.Relation ?? '', GroupName: r.GroupName ?? '',
      EmailId: r.EmailId ?? '', ExpectedCount: r.ExpectedCount ?? 1,
    }));
    checkMobile(r.MobileNumber);
  };

  const checkMobile = async (mobile) => {
    const m = String(mobile || '').trim();
    if (!m) { setDupes([]); setCheckedMobile(''); return []; }
    let res = [];
    try {
      const r = await GetGuestsByEventIdAndMobile({ EventId: event.EventId, MobileNumber: m });
      res = Array.isArray(r) ? r : [];
    } catch { res = []; }
    setDupes(res); setCheckedMobile(m);
    return res;
  };

  // Typing a complete 10-digit number runs the duplicate check straight away.
  useEffect(() => {
    const m = String(form.MobileNumber || '').trim();
    if (!open || editing || familyMode || !/^\d{10}$/.test(m) || m === checkedMobile) return undefined;
    const t = setTimeout(() => { checkMobile(m); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.MobileNumber, open, editing, familyMode, checkedMobile]);
  // The number already belongs to a guest of this event I may edit (mine, or
  // anyone's for an admin) → switch the form to that guest, functions ticked,
  // instead of the "already added" dead end.
  useEffect(() => {
    if (!open || editing || familyMode || !dupes.length) return;
    const d = dupes.find(x => mine(x));
    // The whole list (not the filtered view), grouped like the cards on screen.
    const row = d && combineGuestRows(rows).find(g => (g._guestIds || [g.GuestId]).map(String).includes(String(d.GuestId)));
    if (row) openEdit(row, { existing: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dupes]);

  const blockedSameFn = !editing && !familyMode && dupes.some(
    d => String(d.CreatedBy) === String(me) && rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id)))
  );
  const hasOtherInvites = !editing && dupes.length > 0 && !blockedSameFn;
  const needsCheck = !editing && checkedMobile !== String(form.MobileNumber).trim();

  // ── Add Family MERGES people who are already guests of this event ────────
  // A person already in the list (same mobile; without one, same surname +
  // name) is not created again: the head becomes this family's head, members
  // are MOVED under it (even out of another family), and everyone keeps their
  // functions plus the ones ticked here. Only new people are created.
  const allPeople = useMemo(() => combineGuestRows(rows), [rows]);
  const findExisting = useCallback((mobile, surname, name, exceptId) => {
    const m = String(mobile || '').trim();
    const pool = allPeople.filter(g => String(g.GuestId) !== String(exceptId || ''));
    if (/^\d{10}$/.test(m)) return pool.find(g => String(g.MobileNumber || '') === m) || null;
    const key = `${lc(surname)}|${lc(name)}`;
    if (!lc(name)) return null;
    return pool.find(g => `${lc(g.Surname)}|${lc(g.GuestName)}` === key) || null;
  }, [allPeople]);
  // For the family form: is the head / member i already a guest?
  const familyHeadExisting = familyMode && open && !editing ? findExisting(form.MobileNumber, form.Surname, form.GuestName) : null;
  const familyMemberExisting = (m) => (familyMode ? findExisting(m.MobileNumber, m.Surname || form.Surname, m.GuestName, familyHeadExisting?.GuestId) : null);
  // The people ALREADY in that family, when the typed head turns out to be an
  // existing family head: the form lists them so the user sees who is there
  // before adding more (they are not touched by the save).
  const existingFamilyMembers = useMemo(() => {
    const headId = Number(familyHeadExisting?.GuestId || 0);
    if (!headId) return [];
    const seen = new Set();
    return rows.filter(r => {
      if (Number(r.FamilyId || 0) !== headId) return false;
      if (Number(r.GuestId) === headId) return false;          // the head is row 1
      if (seen.has(String(r.GuestId))) return false;           // one row per person
      seen.add(String(r.GuestId));
      return true;
    });
  }, [rows, familyHeadExisting]);

  const idsOfPerson = (g) => [...new Set([...rowFunctionIds(g), ...(g._functionIds || [])].map(String))];
  const guestIdsOf = (g) => (g._guestIds && g._guestIds.length ? g._guestIds : [g.GuestId]).map(Number);
  // Members of another family whose head is g (g cannot be pulled away then).
  const headsOthers = (g) => Number(g.FamilyId || 0) === Number(g.GuestId)
    && rows.some(x => Number(x.FamilyId || 0) === Number(g.GuestId) && Number(x.GuestId) !== Number(g.GuestId));

  // ── Merge duplicates: other guests with the SAME surname + name ─────────
  // (one person entered several times with different numbers). Opening one of
  // them lists the others; merging keeps the opened guest, gives it every
  // function of the others, moves the families they head under it, notes their
  // numbers in Remarks, keeps the larger extra-card count, and removes them.
  const sameNameGuests = useMemo(() => {
    if (!editing) return [];
    const key = `${lc(editing.Surname)}|${lc(editing.GuestName)}`;
    return allPeople.filter(g => String(g.GuestId) !== String(editing.GuestId)
      && `${lc(g.Surname)}|${lc(g.GuestName)}` === key);
  }, [editing, allPeople]);
  const [mergePick, setMergePick] = useState(() => new Set());
  useEffect(() => { setMergePick(new Set()); }, [editing]);
  const toggleMergePick = (id) => setMergePick(s => { const n = new Set(s); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [merging, setMerging] = useState(false);
  const mergeDuplicates = async () => {
    const keep = editing;
    const dups = sameNameGuests.filter(g => mergePick.has(String(g.GuestId)));
    if (!keep || !dups.length) return;
    if (!dups.every(mine)) { toast('Some of these were added by another member - only an admin can merge them'); return; }
    const who = [keep.Surname, keep.GuestName, keep.FatherHusband].filter(Boolean).join(' ');
    if (!window.confirm(`Merge ${dups.length} duplicate${dups.length > 1 ? 's' : ''} into ${who} (${keep.MobileNumber || 'no number'})?\nTheir functions and family members move here; the duplicates are removed.`)) return;
    setMerging(true);
    try {
      const keepId = Number(keep.GuestId);
      const keepIds = guestIdsOf(keep);
      const fns = new Set(idsOfPerson(keep));
      const numbers = [];
      let extra = Number(keep.ExtraCards || 0);
      let keepFam = Number(keep.FamilyId || 0);
      for (const d of dups) {
        idsOfPerson(d).forEach(f => fns.add(f));
        if (d.MobileNumber && d.MobileNumber !== keep.MobileNumber) numbers.push(d.MobileNumber);
        extra = Math.max(extra, Number(d.ExtraCards || 0));
        const dId = Number(d.GuestId);
        if (Number(d.FamilyId || 0) === dId) {
          // d headed a family: its members (and d itself) move under keep.
          await UpdateGuest({ data: { FamilyId: keepId, UpdatedBy: me }, where: `FamilyId = ${dId} and EventId = ${Number(event.EventId)}` });
          if (!keepFam || keepFam !== keepId) keepFam = keepId;
        } else if (Number(d.FamilyId || 0) && !keepFam) {
          keepFam = Number(d.FamilyId);   // d was on someone's card - keep goes there
        }
      }
      const remark = numbers.length ? `Other numbers: ${[...new Set(numbers)].join(', ')}` : '';
      await UpdateGuest({
        data: {
          FamilyId: keepFam || 0, ExtraCards: extra,
          ...(remark ? { Remarks: [keep.Remarks, remark].filter(Boolean).join(' · ').slice(0, 500) } : {}),
          UpdatedBy: me,
        },
        where: `GuestId in (${keepIds.join(',')})`,
      });
      await SetGuestFunctions({
        GuestId: keepId, EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: [...fns], UpdatedBy: me,
      });
      const dupIds = dups.flatMap(guestIdsOf);
      await UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId in (${dupIds.join(',')})` });
      toast(`Merged ${dups.length} into ${who}${numbers.length ? ` · other numbers kept in Remarks` : ''}`);
      setOpen(false);
      refresh();
    } catch {
      toast('Merge failed - check your connection');
      refresh();
    } finally {
      setMerging(false);
    }
  };

  async function saveFamilyMerged(payload, cardText) {
    const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null;
    const skipped = [];
    let moved = 0;
    let created = 0;
    // ── the head ──
    let headId;
    const exHead = findExisting(form.MobileNumber, form.Surname, form.GuestName);
    if (exHead) {
      headId = Number(exHead.GuestId);
      const famNow = Number(exHead.FamilyId || 0);
      if (famNow && famNow !== headId && headsOthers(exHead)) throw new Error('head-of-other');
      await UpdateGuest({
        data: { FamilyId: headId, ...cardText, UpdatedBy: me },
        where: `GuestId in (${guestIdsOf(exHead).join(',')})`,
      });
      await SetGuestFunctions({
        GuestId: headId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        FunctionIds: [...new Set([...idsOfPerson(exHead), ...selectedFunctionIds])], UpdatedBy: me,
      });
    } else {
      const res = await AddGuestToFunctions({
        ...payload, ...cardText, FunctionIds: selectedFunctionIds, IsFamilyHead: 1, FamilyMembers: [],
        EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        AddedByName: addedByName, AddedByMobile: user.MobileNumber || null, Valid: 1, CreatedBy: me, UpdatedBy: me,
      });
      headId = Number(res?.insertId);
      if (!headId) throw new Error('no-head');
    }
    // ── the members ──
    const filled = party.filter(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
    for (const m of filled) {
      const memberFns = partyFunctionIds(m);
      const ex = findExisting(m.MobileNumber, m.Surname || form.Surname, m.GuestName, headId);
      const label = [m.Surname || form.Surname, m.GuestName].filter(Boolean).join(' ');
      try {
        if (ex) {
          if (headsOthers(ex)) { skipped.push(`${label} (head of another family)`); continue; }
          if (Number(ex.FamilyId || 0) && Number(ex.FamilyId) !== headId) {
            // Out of the old family first - the move only takes free guests.
            await UpdateGuest({ data: { FamilyId: 0, UpdatedBy: me }, where: `GuestId in (${guestIdsOf(ex).join(',')})` });
          }
          await AddOrMoveFamilyMember({
            HeadGuestId: headId, ExistingGuestId: Number(ex.GuestId), EventId: event.EventId,
            OrganiserId: organiser.OrganiserId, CreatedBy: me, UpdatedBy: me,
          });
          await SetGuestFunctions({
            GuestId: Number(ex.GuestId), EventId: event.EventId, OrganiserId: organiser.OrganiserId,
            FunctionIds: [...new Set([...idsOfPerson(ex), ...memberFns])], UpdatedBy: me,
          });
          moved += 1;
        } else {
          const res = await AddOrMoveFamilyMember({
            HeadGuestId: headId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
            FunctionIds: memberFns, Surname: m.Surname || form.Surname || null, GuestName: m.GuestName || null,
            MobileNumber: m.MobileNumber ? ten(m.MobileNumber) : null,
            Side: payload.Side, Visibility: payload.Visibility, AddedByName: addedByName,
            AddedByMobile: user.MobileNumber || null, CreatedBy: me, UpdatedBy: me,
          });
          if (res?.action === 'moved' || res?.action === 'already-member') moved += 1; else created += 1;
        }
      } catch (err) {
        skipped.push(`${label} (${err?.response?.data?.error || 'could not save'})`);
      }
    }
    const headName = [form.Surname, form.GuestName].filter(Boolean).join(' ');
    const parts = [`${headName}'s family saved${exHead ? ' (existing guest made head)' : ''}`];
    if (moved) parts.push(`${moved} moved in`);
    if (created) parts.push(`${created} added`);
    if (skipped.length) parts.push(`not moved: ${skipped.join(', ')}`);
    return { message: parts.join(' · ') };
  }

  // save({ addNext:true }) keeps the form open for the next guest: functions,
  // side and visibility stay, name/mobile clear, the list refreshes behind.
  const save = async ({ addNext = false } = {}) => {
    if (!selectedFunctionIds.length) return toast('Pick at least one function');
    if (!String(form.GuestName).trim()) return toast('Name is required');
    // A phone number is OPTIONAL: many relatives are invited in person or by
    // card. When one IS given it must be a real 10-digit mobile, because that
    // is what the WhatsApp invite and the duplicate check work on.
    const headMobile = String(form.MobileNumber || '').trim();
    if (headMobile && !/^\d{10}$/.test(headMobile)) return toast('A mobile number must be 10 digits');
    // The same name for the same function is a repeat, not a new guest.
    // (Add Family merges existing people instead - see saveFamilyMerged.)
    if (!editing && !familyMode) {
      const sameName = clashingFunctions(nameMatches(form.Surname, form.GuestName));
      if (sameName.length) {
        return toast(`Already saved - ${[form.Surname, form.GuestName].filter(Boolean).join(' ')} is invited to ${sameName[0].FunctionName || fnName(sameName[0].FunctionId)} (added by ${whoAdded(sameName[0])})`);
      }
      // Two members of one family may share a name; two of them may not share
      // a number, so the family rows are checked against each other too.
      const dupWithin = party.filter(m => String(m.GuestName || '').trim())
        .find((m, i, all) => all.findIndex(x => lc(x.GuestName) === lc(m.GuestName)) !== i);
      if (dupWithin) return toast(`${dupWithin.GuestName} is listed twice in this family`);
    }
    if (familyMode) {
      // A family may be saved with the HEAD ALONE - members are often added
      // later, from the family's own screen. Only rows that were half-filled
      // are refused: a name is what makes a member.
      const filled = party.filter(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
      if (filled.some(m => !String(m.GuestName || '').trim())) {
        return toast('Give a name for every family member, or clear the empty row');
      }
      const badMobile = party.find(m => String(m.MobileNumber || '').trim() && !/^\d{10}$/.test(String(m.MobileNumber).trim()));
      if (badMobile) return toast('A family member\'s mobile number must be 10 digits');
    }

    setSaving(true);
    try {
      // One-click save: dedup-check inline. Block only a true duplicate (the same
      // mobile already in MY list for THIS function); otherwise save right away.
      let dupeRows = dupes;
      if (!editing && !familyMode && headMobile) {
        dupeRows = await checkMobile(form.MobileNumber);
        // Whoever added it: one number belongs to one person, so the same
        // number for the same function is the same invitation twice.
        const blocked = dupeRows.find(d => rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id))));
        if (blocked) {
          setSaving(false);
          return toast(`Already saved - this number is invited to ${blocked.FunctionName || fnName(blocked.FunctionId)} (added by ${whoAdded(blocked)})`);
        }
      }
      const dupExists = !editing && dupeRows.length > 0;
      // eslint-disable-next-line no-unused-vars
      const { FunctionIds, ExtraCards, CardMode, CardFamilyId, CardHeadText, CardName, CardNameAuto, CardDeliveredByName, ...singleForm } = form;
      // The household's card (Cards page fields). On ANOTHER head's card the
      // card text belongs to that head, so nothing of it is written here.
      // "On the card of …": a typed name that matches exactly one head is
      // accepted; otherwise the save is refused - never silently "own card".
      let resolvedHead = CardFamilyId;
      if (!familyMode && CardMode === 'family' && !resolvedHead) {
        const typed = lc(CardHeadText).split('·')[0].trim();
        const hits = typed ? cardHeadOptions.filter(o => lc(o.label).split('·')[0].trim() === typed
          || lc([o.head.Surname, o.head.GuestName].filter(Boolean).join(' ')) === typed) : [];
        if (hits.length === 1) resolvedHead = hits[0].value;
        else {
          setSaving(false);
          return toast(hits.length > 1
            ? `${hits.length} guests are called "${CardHeadText}" - pick the right one from the list (the mobile number tells them apart)`
            : 'Pick whose card this guest is on from the list, or choose Own card');
        }
      }
      const onOtherCard = !familyMode && CardMode === 'family' && !!resolvedHead;
      const cardText = onOtherCard ? {} : {
        CardName: CardNameAuto ? null : (String(CardName || '').trim() || null),
        CardDeliveredByName: String(CardDeliveredByName || '').trim() || null,
        ...(Number(ExtraCards) > 0 ? { ExtraCards: Math.min(99, Number(ExtraCards)) } : (editing ? { ExtraCards: 0 } : {})),
      };
      const target = onOtherCard ? cardHeadOptions.find(f => f.value === String(resolvedHead)) : null;
      if (onOtherCard && !target) { setSaving(false); return toast('Pick whose card this guest is on'); }
      if (onOtherCard && editing && target.guestIds && target.guestIds.map(String).includes(String(editing.GuestId))) {
        setSaving(false); return toast('A guest cannot be on their own card - choose Own card');
      }
      const editFam = editing ? Number(editing.FamilyId || 0) : 0;
      const editIsHead = !!editing && editFam && editFam === Number(editing.GuestId);
      const editIds = editing ? (editing._guestIds?.length ? editing._guestIds : [editing.GuestId]) : [];
      if (onOtherCard && editing && String(editFam) !== String(CardFamilyId)) {
        const headOfOthers = editIsHead && rows.some(x => Number(x.FamilyId || 0) === editFam && Number(x.GuestId) !== Number(editing.GuestId));
        if (headOfOthers) { setSaving(false); return toast('This guest is the head of a family card - move the family members first'); }
      }
      if (onOtherCard && target.size >= MAX_FAMILY_SIZE && !(editing && String(editFam) === String(resolvedHead))) {
        setSaving(false); return toast(`That card already has ${target.size} people (max ${MAX_FAMILY_SIZE})`);
      }
      // The family id this guest will carry; an individual chosen as head
      // becomes the head of a new family (their FamilyId = their GuestId).
      let joinFamilyId = onOtherCard ? Number(String(resolvedHead).replace(/^g:/, '')) : 0;
      if (onOtherCard && String(resolvedHead).startsWith('g:')) {
        await UpdateGuest({ data: { FamilyId: joinFamilyId, UpdatedBy: me }, where: `GuestId in (${target.guestIds.map(Number).join(',')})` });
      }
      // lockedSide wins over whatever the form holds: a member can only ever
      // create guests for their own side (admins are not locked).
      const payload = emptyToNull({ ...singleForm, Side: isMarriage ? (lockedSide || singleForm.Side) : 'Common', ExpectedCount: 1 });
      if (editing) {
        await UpdateGuest({ data: { ...payload, UpdatedBy: me }, where: `GuestId = ${editing.GuestId}` });
        await SetGuestFunctions({
          GuestId: editing.GuestId,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          FunctionIds: selectedFunctionIds,
          UpdatedBy: me,
        });
        // Whose card: join another head's card, or back to an own card.
        const famData = onOtherCard
          ? (editFam !== joinFamilyId ? { FamilyId: joinFamilyId } : {})
          : (editFam && !editIsHead ? { FamilyId: 0 } : {});
        if (Object.keys(famData).length) {
          await UpdateGuest({ data: { ...famData, UpdatedBy: me }, where: `GuestId in (${editIds.map(Number).join(',')})` });
        }
        // Own card text: a family head's card text goes to the whole family.
        if (!onOtherCard) {
          await UpdateGuest({
            data: { ...cardText, UpdatedBy: me },
            where: editIsHead ? `FamilyId = ${Number(editFam)} and EventId = ${Number(event.EventId)}` : `GuestId in (${editIds.map(Number).join(',')})`,
          });
        }
        toast('Updated');
      } else if (familyMode) {
        const r = await saveFamilyMerged(payload, cardText);
        toast(r.message);
      } else {
        const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || '';
        const familyMembers = familyMode
          ? party.filter(m => String(m.GuestName || '').trim() || String(m.Surname || '').trim())
          // Family members are asked for a name only - they carry the head's
          // surname unless one was typed for them.
          .map(m => ({
            Surname: m.Surname || form.Surname || null,
            GuestName: m.GuestName || null,
            MobileNumber: m.MobileNumber ? ten(m.MobileNumber) : null,
            // Only the functions this person is invited to (empty = the head's).
            FunctionIds: partyFunctionIds(m),
          }))
          : [];
        const res = await AddGuestToFunctions({
          ...payload,
          ...cardText,
          FunctionIds: selectedFunctionIds,
          // Saved as a family even with no members yet: the head becomes the
          // family, and the rest of the household is added on the Families
          // screen later. One family = one invitation card.
          IsFamilyHead: familyMode ? 1 : 0,
          FamilyMembers: familyMembers,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          AddedByName: addedByName || null,
          AddedByMobile: user.MobileNumber || null,
          Valid: 1,
          CreatedBy: me,
          UpdatedBy: me,
        });
        // On another head's card: the new guest joins that family.
        if (onOtherCard) {
          const newIds = (Array.isArray(res?.insertedIds) && res.insertedIds.length ? res.insertedIds : [res?.insertId]).filter(Boolean);
          if (newIds.length) {
            await UpdateGuest({ data: { FamilyId: joinFamilyId, UpdatedBy: me }, where: `GuestId in (${newIds.map(Number).join(',')})` });
          }
        }
        const familyRows = res?.familyRows || 1;
        toast(familyMode
          ? `Family of ${familyRows} added`
          : (dupExists ? 'Guest added to selected functions' : 'Guest added'));
      }
      // Remember what was used so the next guest starts from the same choices.
      writeDefaults(event.EventId, { FunctionIds: selectedFunctionIds, Side: form.Side, Visibility: form.Visibility });
      if (addNext && !editing) {
        setAddedThisSession(n => n + 1);
        // Keep what does not change between two people of one house: the
        // functions, the side, the visibility AND the surname. Only the name
        // and the number are typed again.
        setForm(f => ({
          ...blankForm(),
          FunctionIds: f.FunctionIds, FunctionId: f.FunctionId,
          Side: f.Side, Visibility: f.Visibility, Surname: f.Surname,
        }));
        setParty(familyMode ? blankMembers() : []);
        setAddMode('new'); setDupes([]); setCheckedMobile('');
        refresh();
        return;
      }
      setOpen(false); refresh();
    } catch (err) {
      toast(err?.message === 'head-of-other'
        ? 'This head is already head of another family - open that family instead'
        : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Functions ticked straight in the guest table (desktop) ───────────────
  // One checkbox per function on each row; ticking saves at once for that
  // guest (SetEvt_GuestFunctions), with the Wedding rule. A guest saved
  // without the Wedding had it removed on purpose, so it is not re-added.
  const [savingFn, setSavingFn] = useState('');   // `${GuestId}:${FunctionId}` being saved
  const rowHasFunction = (row, functionId) => rowFunctionIds(row).map(String).includes(String(functionId))
    || (row._functionIds || []).map(String).includes(String(functionId));
  const toggleRowFunction = async (row, functionId) => {
    if (!(mine(row) && canEdit) || savingFn) return;
    const fn = functions.find(f => String(f.FunctionId) === String(functionId));
    if (!fn || isPastFunction(fn)) return;
    const current = [...new Set([...rowFunctionIds(row), ...(row._functionIds || [])].map(String))];
    const hadWedding = current.some(id => isWeddingFunction(functions.find(f => String(f.FunctionId) === id)));
    const res = toggleWithWedding(current, functionId, functions, current.length > 0 && !hadWedding);
    if (!res.ids.length) { toast('A guest needs at least one function - delete the guest instead'); return; }
    const names = functions.filter(f => res.ids.includes(String(f.FunctionId))).map(f => f.FunctionName).join(', ');
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(String);
    // Show it at once; the list reloads quietly afterwards.
    setRows(rs => rs.map(x => (ids.includes(String(x.GuestId)) ? { ...x, FunctionIds: res.ids.join(','), FunctionNames: names, FunctionName: names } : x)));
    setSavingFn(`${row.GuestId}:${functionId}`);
    try {
      await SetGuestFunctions({
        GuestId: row.GuestId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        FunctionIds: res.ids, UpdatedBy: me,
      });
    } catch {
      toast('Could not save - check your connection');
    } finally {
      setSavingFn('');
      refresh(true);
    }
  };

  // Public / Private checkbox column: one guest, or every guest on screen at
  // once (header box - pick "Added by: Haresh" first to mark only his).
  // Public = Visibility 'Common' (all members see it), else 'Private'.
  // Only guests this user may edit are changed.
  // Guest Type column (Relative / Friend / Business): saved at once. The firm
  // of a business guest is still typed in the guest form.
  const setGuestTypeFor = async (row, type) => {
    if (!(mine(row) && canEdit)) return;
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(Number).filter(Boolean);
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, GuestType: type } : x)));
    try {
      await UpdateGuest({ data: { GuestType: type, UpdatedBy: me }, where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}` });
    } catch {
      toast('Could not save - check your connection');
    } finally {
      refresh(true);
    }
  };
  const [savingVis, setSavingVis] = useState(false);
  const isPublic = (row) => (row.Visibility || 'Private') === 'Common';
  const setVisibilityFor = async (list, makePublic) => {
    if (!canEdit || savingVis) return;
    const value = makePublic ? 'Common' : 'Private';
    const targets = (list || []).filter(r => mine(r) && isPublic(r) !== makePublic);
    const ids = [...new Set(targets.flatMap(r => (r._guestIds && r._guestIds.length ? r._guestIds : [r.GuestId])).map(Number).filter(Boolean))];
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, Visibility: value } : x)));
    setSavingVis(true);
    try {
      await UpdateGuest({ data: { Visibility: value, UpdatedBy: me }, where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}` });
      if (targets.length > 1) toast(`${targets.length} guests marked ${makePublic ? 'Public' : 'Private'}`);
    } catch {
      toast('Could not save - check your connection');
    } finally {
      setSavingVis(false);
      refresh(true);
    }
  };


  // Card of a guest (table column): head of a family, on someone's card, or own.
  const cardInfo = useCallback((row) => {
    const famId = Number(row.FamilyId || 0);
    if (!famId) return { kind: 'own', extra: Number(row.ExtraCards || 0) };
    const fam = familyOptions.find(f => Number(f.familyId) === famId);
    if (famId === Number(row.GuestId)) return { kind: 'head', size: fam ? fam.size : 1, extra: Number(row.ExtraCards || 0) };
    const head = fam && fam.head;
    return { kind: 'member', headName: head ? [head.Surname, head.GuestName].filter(Boolean).join(' ') : 'family', extra: 0 };
  }, [familyOptions]);

  // What the Share / Export dialog prints for this list: the columns a guest
  // list is actually read for, over the rows the filters leave on screen - so
  // the file matches what the page shows, filter for filter. Remarks is left
  // out on purpose: it holds working notes (merged numbers and such), not
  // something to hand around on a printed list.
  // Groom / Bride in one phrase: the chosen filter, or what the rows actually
  // are when nothing is filtered.
  const sideLabel = useMemo(() => {
    if (!isMarriage) return '';
    if (sideFilter && sideFilter !== 'all') return `${sideFilter} side`;
    const kinds = [...new Set(filtered.map(r => sideKey(r.Side)).filter(k => k && k !== 'Common'))];
    if (kinds.length === 1) return `${kinds[0]} side`;
    return kinds.length > 1 ? 'Groom & Bride' : '';
  }, [isMarriage, sideFilter, filtered]);

  const report = useMemo(() => ({
    title: 'Guests',
    subtitle: [
      fnFilterLabel,
      // Said ONCE in the header, never as a column repeating down every page:
      // which side these guests are, and whose list it is.
      sideLabel,
      (guestTypeFilter === 'business' || listView === 'business') ? 'Business associates' : '',
      adder ? `Added by: ${adder.label}` : '',
      q.trim() ? `search "${q.trim()}"` : '',
    ].filter(Boolean).join(' · '),
    columns: [
      { key: 'Surname', label: 'Surname' },
      { key: 'GuestName', label: 'Name' },
      { key: 'MobileNumber', label: 'Mobile' },
      { key: 'Functions', label: 'Invited to', exportValue: (r) => r.FunctionNames || r.FunctionName || fnName(r.FunctionId) },
      { key: 'Card', label: 'Card', exportValue: (r) => {
        const info = cardInfo(r);
        if (info.kind === 'head') return `Head · ${info.size}${info.extra ? ` (+${info.extra})` : ''}`;
        if (info.kind === 'member') return `On ${info.headName}`;
        return info.extra ? `Own (+${info.extra})` : 'Own';
      } },
      // No "Added by" column: the printed list is handed around, who added a
      // guest is not for it (a one-person filter still names them in the header).
    ],
    rows: filtered,
  }), [filtered, fnFilterLabel, fnName, adder, sideLabel, guestTypeFilter, listView, q, cardInfo]);

  const mark = async (row, status) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { InviteStatus: status, UpdatedBy: me }, where: `GuestId = ${id}` })));
      setRows(rs => rs.map(r => ids.includes(r.GuestId) ? { ...r, InviteStatus: status } : r));
    } catch { toast('Could not update status'); }
  };

  const del = async (row) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    const suffix = ids.length > 1 ? ` from ${ids.length} functions` : '';
    if (!window.confirm(`Remove ${row.GuestName || 'this member'}${suffix} from the list?`)) return;
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId = ${id}` })));
      toast('Removed'); refresh();
    } catch { toast('Remove failed'); }
  };

  const guestHasAllFunctions = (row) => {
    if (!functions.length) return false;
    const ids = rowFunctionIds(row).map(String);
    return functions.every(f => ids.includes(String(f.FunctionId)));
  };

  const openInvite = (row) => {
    setInviteRow(row);
  };
  const closeInvite = () => {
    if (inviteSharing) return;
    setInviteRow(null);
  };
  const sendInvite = async (kind) => {
    if (!inviteRow) return;
    // A guest may be saved without a number (invited in person or by card) -
    // there is then nowhere to send a WhatsApp invite.
    if (!String(inviteRow.MobileNumber || '').replace(/\D/g, '')) {
      toast('This guest has no mobile number - add one to send a WhatsApp invite');
      return;
    }
    if (kind === 'pdf' && !guestHasAllFunctions(inviteRow)) {
      toast('PDF invite is only for guests invited to all functions');
      return;
    }
    setInviteSharing(kind);
    try {
      await shareInvite(kind, inviteRow, event, organiser);
      await mark(inviteRow, 'Invited');
      setInviteCooldown(INVITE_COOLDOWN_SECONDS);
      toast('Invite sent');
    } catch (err) {
      toast(err?.message || 'WhatsApp invite failed');
    } finally {
      setInviteSharing('');
    }
  };

  const addOrMoveFamilyMember = async (head, member) => {
    const headName = [head.Surname, head.GuestName].filter(Boolean).join(' ').trim() || 'the family head';
    try {
      const result = await AddOrMoveFamilyMember({
        HeadGuestId: head.GuestId,
        ExistingGuestId: member.ExistingGuestId || null,
        EventId: event.EventId,
        OrganiserId: organiser.OrganiserId,
        FunctionIds: rowFunctionIds(head),
        Surname: member.Surname || null,
        GuestName: member.GuestName || null,
        MobileNumber: member.MobileNumber || null,
        Side: member.Side || head.Side,
        Visibility: member.Visibility || head.Visibility,
        Remarks: member.Remarks || null,
        AddedByName: `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null,
        AddedByMobile: user.MobileNumber || null,
        CreatedBy: me,
        UpdatedBy: me,
      });
      const memberName = [result?.Surname, result?.GuestName].filter(Boolean).join(' ').trim() || 'Guest';
      if (result?.action === 'moved') toast(`${memberName} was moved to ${headName}'s Family`);
      else if (result?.action === 'already-member') toast(`${memberName} is already in ${headName}'s Family`);
      else toast(`${memberName} was added to ${headName}'s Family`);
      refresh();
      return result;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Could not add family member';
      toast(message);
      throw err;
    }
  };

  // setMobile keeps the dedup state honest when the number changes.
  const setMobile = (v) => { setF('MobileNumber', ten(v)); if (checkedMobile) { setDupes([]); setCheckedMobile(''); } };

  // ── Bulk import from the phone book ─────────────────────────────────────────
  const openBulk = async () => {
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const remembered = readDefaults(event.EventId);
    const rememberedIds = (remembered.FunctionIds || []).map(String).filter(id => allOpenIds.includes(id));
    setBulkSelected(new Set()); setBulkSearch(''); setBulkFunctionIds(newGuestFunctionIds(preset && !isPastFunction(preset) ? [String(fnFilter)] : rememberedIds, functions));
    setBulkWeddingOff(false);
    setBulkSide(lockedSide || (isMarriage && remembered.Side) || guestSide); setBulkVisibility(remembered.Visibility || 'Private'); setBulkOpen(true);
    setBulkLoading(true);
    try {
      const list = await getDeviceContacts();
      setDeviceContacts(list);
    } catch (e) {
      toast(e.message || 'Could not read contacts'); setBulkOpen(false);
    } finally { setBulkLoading(false); }
  };
  const toggleBulkFunction = (functionId) => {
    const res = toggleWithWedding(bulkFunctionIds, functionId, functions, bulkWeddingOff);
    setBulkWeddingOff(res.weddingOff);
    setBulkFunctionIds(res.ids);
  };
  const toggleBulk = (key) => setBulkSelected(s => {
    const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });
  // mobile → Set(FunctionId) already in MY list, so the picker can show "Added"
  // and only add the missing functions for a contact.
  const myFnsByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (String(r.CreatedBy) !== String(me) || !r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      rowFunctionIds(r).forEach(id => m.get(r.MobileNumber).add(String(id)));
    });
    return m;
  }, [rows, me]);
  // Functions (of the chosen ones) this contact still needs; [] = fully added already.
  const missingFnsFor = (c) => {
    const have = c.mobile ? myFnsByMobile.get(c.mobile) : null;
    return bulkFunctionIds.filter(id => !(have && have.has(String(id))));
  };
  const bulkVisible = useMemo(() => {
    const s = bulkSearch.trim().toLowerCase();
    const list = s ? deviceContacts.filter(c => `${c.display} ${c.mobile}`.toLowerCase().includes(s)) : deviceContacts;
    return list.map(c => ({ ...c, added: bulkFunctionIds.length > 0 && missingFnsFor(c).length === 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceContacts, bulkSearch, bulkFunctionIds, myFnsByMobile]);
  const bulkSelectable = bulkVisible.filter(c => !c.added);
  const bulkAllVisibleSelected = bulkSelectable.length > 0 && bulkSelectable.every(c => bulkSelected.has(c.key));
  const toggleBulkAll = () => setBulkSelected(s => {
    const n = new Set(s);
    if (bulkAllVisibleSelected) bulkSelectable.forEach(c => n.delete(c.key));
    else bulkSelectable.forEach(c => n.add(c.key));
    return n;
  });

  const saveBulk = async () => {
    if (!bulkFunctionIds.length) return toast('Pick at least one function');
    const chosen = deviceContacts.filter(c => bulkSelected.has(c.key));
    if (!chosen.length) return toast('Select at least one contact');
    setBulkSaving(true);
    setBulkProgress({ done: 0, total: chosen.length });
    let people = 0, invites = 0, skipped = 0;
    try {
      const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || '';
      // ONE API call per contact with only the functions they are not yet in
      // (the API inserts one Evt_Guests row per function).
      for (const c of chosen) {
        const fnIds = missingFnsFor(c);
        if (!fnIds.length) { skipped++; setBulkProgress(p => ({ ...p, done: p.done + 1 })); continue; }
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: fnIds,
          Surname: c.surname || null, GuestName: c.name || c.display || null, FatherHusband: c.fatherHusband || null,
          MobileNumber: c.mobile || null, Side: isMarriage ? (lockedSide || bulkSide) : 'Common', Visibility: bulkVisibility, ExpectedCount: 1,
          InviteStatus: 'Pending', AddedByName: addedByName || null, AddedByMobile: user.MobileNumber || null,
          Valid: 1, CreatedBy: me, UpdatedBy: me,
        });
        people++; invites += fnIds.length;
        setBulkProgress(p => ({ ...p, done: p.done + 1 }));
      }
      writeDefaults(event.EventId, { FunctionIds: bulkFunctionIds, Side: bulkSide, Visibility: bulkVisibility });
      toast(`Added ${people} guest${people === 1 ? '' : 's'} (${invites} invite${invites === 1 ? '' : 's'})${skipped ? ` · ${skipped} already in your list` : ''}`);
      setBulkOpen(false); refresh();
    } catch {
      toast(`Added ${people}, then a save failed — check your connection and try again`);
      refresh();
    } finally { setBulkSaving(false); }
  };

  // "Find person" in the add-guest form: picking someone known (a guest of any
  // event of this organiser, or a synced phone contact) fills the head fields.
  const people = usePeopleSearch({
    organiserId: organiser.OrganiserId, userId: me, allGuests: isAdmin,
    onPick: (p) => fillHead(p),
  });

  // Picking a person fills the head (or a family member's row) in one go.
  function fillHead(p) {
    setForm(f => ({
      ...f, Surname: nameInput(p.Surname || ''), GuestName: nameInput(p.GuestName || ''),
      FatherHusband: normaliseText(p.FatherHusband || ''), MobileNumber: ten(p.MobileNumber || ''),
    }));
    if (p.MobileNumber) checkMobile(ten(p.MobileNumber));
  }
  // Type-ahead from MY synced phone book in Surname / Name / Father / Mobile
  // (target 'head' = the guest or family head, a number = that family member).
  const contactSuggest = useContactSuggest({
    organiserId: organiser.OrganiserId, userId: me,
    onPick: (p, target) => {
      if (target === 'head' || target == null) { fillHead(p); return; }
      setParty(list => list.map((m, i) => (i === target ? {
        ...m, Surname: nameInput(p.Surname || ''), GuestName: nameInput(p.GuestName || ''), MobileNumber: ten(p.MobileNumber || ''),
      } : m)));
    },
  });

  // Fast grid entry of many guests (see useQuickAdd).
  const quick = useQuickAdd({
    event, organiser, user, functions, rows, isMarriage, lockedSide, defaultSide, sideMissing,
    fnFilter, refresh, toast, readDefaults, writeDefaults, rowFunctionIds,
  });

  return {
    quick, people, contactSuggest, listView, setListView, familyGroups, individualRows, businessRows, viewCounts, openFamilyFor, familyHeadNote, sameNameGuests, mergePick, toggleMergePick, mergeDuplicates, merging, familyHeadExisting, familyMemberExisting, existingFamilyMembers, toggleRowFunction, rowHasFunction, savingFn, isPublic, setVisibilityFor, savingVis, setGuestTypeFor, visFilter, setVisFilter, visCounts, typeCounts, cardInfo, existingNotice, deliveryNames, cardHeadOptions,
    isAdmin, canView, canAdd, canEdit, canDelete, mine, isMarriage, sideOptions, lockedSide, sideMissing,
    functions, invitableFunctions, pickerFunctions, rows, loading, q, setQ, fnFilter, setFnFilter, fnCount, fnMode, setFnMode, fnFilterLabel, sideFilter, setSideFilter, sideCounts, cardFilter, setCardFilter, cardCounts, fnName,
    filtered, stats, adderCount, distinctPeople, sort, setSort, toggleSort, SORT_OPTIONS, report,
    adderOptions, adderFilter, setAdderFilter, adder,
    familyCards, visibleCards, guestTypeFilter, setGuestTypeFilter, isBusiness, actionCooldown, beginFamilyAction, callGuest,
    open, setOpen, editing, form, setF, setMobile, selectedFunctionIds, toggleFunction,
    party, addParty, setPartyField, removeParty, togglePartyFunction, partyFunctionIds, familyMode,
    addMode, setAddMode, dupes, checkedMobile, blockedSameFn, hasOtherInvites, needsCheck,
    openAdd, openEdit, clearForm, isDirty, canImport, importContact, importPartyContact,
    numberChoice, chooseContactNumber, cancelNumberChoice: () => setNumberChoice(null),
    knownSurnames, knownNames, nameMatches, clashingFunctions, familyOptions,
    pickExisting, save, saving, mark, del, me,
    allFunctionsSelected, toggleAllFunctions, addedThisSession,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    addOrMoveFamilyMember,
    // bulk import
    canBulkImport, bulkOpen, setBulkOpen, bulkLoading, bulkSaving, bulkProgress, deviceContacts, bulkVisible, bulkSelectable,
    bulkSelected, toggleBulk, bulkSearch, setBulkSearch, bulkAllVisibleSelected, toggleBulkAll,
    bulkFunctionIds, setBulkFunctionIds, toggleBulkFunction, bulkSide, setBulkSide, bulkVisibility, setBulkVisibility,
    openBulk, saveBulk,
  };
}
